import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ASSETS, formatCPF, onlyDigits } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Lock, ShieldCheck, Info } from "lucide-react";
import Header from "@/components/Header";
import GoogleLoginButton from "@/components/GoogleLoginButton";

function LoginPanel() {
  return (
    <div className="hidden lg:flex relative items-center justify-center bg-gradient-to-br from-[#083CA6] to-[#00883A] p-12 text-white">
      <div className="max-w-md">
        <img src={ASSETS.logoMcmv} alt="MCMV" className="h-20 w-20 mb-6" />
        <h2 className="text-4xl font-black mb-3">Acesso Restrito</h2>
        <p className="text-white/90">Portal oficial de gestão das pré-inscrições do Programa Minha Casa Minha Vida 2026 — Prefeitura Municipal de Grajaú/MA.</p>
        <div className="mt-6 inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold border border-white/20">
          <ShieldCheck className="w-3.5 h-3.5" /> Uso exclusivo para servidores autorizados
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [cpf, setCpf] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(onlyDigits(cpf), password);
      toast.success("Bem-vindo!");
      nav(u.role === "cidadao" ? "/area-cidadao" : "/admin");
    } catch (err) {
      logger.error("Falha de autenticação", err);
      toast.error(err.response?.data?.detail || "CPF ou senha inválidos");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F6F9] flex flex-col">
      <Header />
      <div className="flex-1 grid lg:grid-cols-2">
        <LoginPanel />
        <div className="flex items-center justify-center p-6 md:p-12">
          <Card className="w-full max-w-md" data-testid="login-card">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#083CA6]" /> Entrar no Sistema
              </CardTitle>
              <CardDescription>Acesso exclusivo para servidores cadastrados pelo Super Administrador.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="cpf">CPF</Label>
                  <Input id="cpf" value={cpf} onChange={(e) => setCpf(formatCPF(e.target.value))} placeholder="000.000.000-00" required data-testid="login-cpf-input" />
                </div>
                <div>
                  <Label htmlFor="password">Senha</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="login-password-input" />
                </div>
                <Button type="submit" className="w-full bg-[#083CA6] hover:bg-[#062f85]" disabled={loading} data-testid="login-submit-btn">
                  {loading ? "Aguarde..." : "Entrar"}
                </Button>
              </form>

              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">
                    Cidadão? use seu Google
                  </span>
                </div>
              </div>
              <GoogleLoginButton
                className="w-full"
                label="Entrar com Google (cidadão)"
                dataTestId="btn-google-login-main"
              />

              <Alert className="mt-5 border-[#083CA6]/30 bg-[#083CA6]/5">
                <Info className="w-4 h-4 text-[#083CA6]" />
                <AlertDescription className="text-xs text-slate-700">
                  <strong>É cidadão?</strong> Você não precisa de conta. Faça sua inscrição pública e acompanhe pelo número do protocolo.
                  <div className="mt-2 flex gap-2">
                    <Link to="/inscricao" className="text-[#083CA6] font-semibold hover:underline">Fazer Inscrição</Link>
                    <span>·</span>
                    <Link to="/consultar" className="text-[#083CA6] font-semibold hover:underline">Consultar Protocolo</Link>
                  </div>
                </AlertDescription>
              </Alert>

              <div className="mt-4 text-center">
                <Link to="/" className="text-xs text-slate-500 hover:underline">Voltar à página inicial</Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
